import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { Page, ResponseError } from '../type/type';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';

export interface IProductInfo {
  id: string;
  name: string;
  custInfoCount?: 1 | 2;
}

export interface IPurchaseLandingTrackingCodePackage {
  id: number;
  packageName: string;
  packagePrice: number;
  personType?: 1 | 2;
  products?: IProductInfo[];
}

export interface IPurchaseLandingTrackingCode {
  id: number;
  trkId: string;
  campaignName: string;
  personType: number; // 1 or 2
  showFooter?: boolean;
  packages: IPurchaseLandingTrackingCodePackage[];
  topImageUrls?: string[];
  bottomImageUrls?: string[];
  facebookPixelId?: string;
  kakaoPixelId?: string;
  googleAdsPixelId?: string;
  googleAdsEventSnippet?: string;
  daangnPixelId?: string;
  tiktokPixelId?: string;
  bankName?: string;
  bankAccount?: string;
  bankAccountHolder?: string;
  purchaseButtonName?: string;
  documentTitle?: string;
  franchiseId?: string;
  companyName?: string; // 가맹점 회사명
  landedUserCount: number;
  resultUserCount: number;
  orderCreatedCount: number;
  createdAt: string;
}

export interface IPackageInput {
  id?: number; // 수정 시 기존 패키지 ID (null이면 신규)
  packageName: string;
  packagePrice: number;
  personType?: 1 | 2;
  productIds?: string[]; // 연결할 실제 상품 ID 목록
}

export interface IPurchaseLandingTrackingCodeCreateRequest {
  campaignName: string;
  personType?: number;
  showFooter?: boolean;
  packages: IPackageInput[];
  topImageUrls?: string[];
  bottomImageUrls?: string[];
  facebookPixelId?: string;
  kakaoPixelId?: string;
  googleAdsPixelId?: string;
  googleAdsEventSnippet?: string;
  daangnPixelId?: string;
  tiktokPixelId?: string;
  bankName?: string;
  bankAccount?: string;
  bankAccountHolder?: string;
  purchaseButtonName?: string;
  documentTitle?: string;
}

export interface IPurchaseLandingTrackingCodeUpdateRequest {
  campaignName?: string;
  personType?: number;
  showFooter?: boolean;
  packages?: IPackageInput[];
  topImageUrls?: string[];
  bottomImageUrls?: string[];
  facebookPixelId?: string;
  kakaoPixelId?: string;
  googleAdsPixelId?: string;
  googleAdsEventSnippet?: string;
  daangnPixelId?: string;
  tiktokPixelId?: string;
  bankName?: string;
  bankAccount?: string;
  bankAccountHolder?: string;
  purchaseButtonName?: string;
  documentTitle?: string;
}

export interface IPurchaseLandingTrackingCodeSearch {
  campaignName?: string;
  trkId?: string;
  franchiseIds?: string[]; // ROLE_ADMIN 전용
}

interface PurchaseLandingTrackingCodeResponse extends Page {
  content: IPurchaseLandingTrackingCode[];
}

type PurchaseLandingTrackingCodeState = {
  codeList: IPurchaseLandingTrackingCode[];
  codePage: Page | null;
  loading: boolean;
  search: IPurchaseLandingTrackingCodeSearch;
};

type PurchaseLandingTrackingCodeAction = {
  fetchCodes: (page?: number, size?: number) => Promise<void>;
  setSearch: (search: IPurchaseLandingTrackingCodeSearch) => void;
  createCode: (payload: IPurchaseLandingTrackingCodeCreateRequest) => Promise<void>;
  updateCode: (id: number, payload: IPurchaseLandingTrackingCodeUpdateRequest) => Promise<boolean>;
  deleteCodes: (ids: number[]) => Promise<boolean>;
  reset: () => void;
};

export const usePurchaseLandingTrackingStore = create<
  PurchaseLandingTrackingCodeState & PurchaseLandingTrackingCodeAction
>((set, get) => ({
  codeList: [],
  codePage: null,
  loading: false,
  search: {},

  setSearch: (search: IPurchaseLandingTrackingCodeSearch) => {
    set({ search });
  },

  fetchCodes: async (page = 0, size = 20) => {
    set({ loading: true });
    try {
      const { search } = get();
      const response = await apiClient.get<PurchaseLandingTrackingCodeResponse>(
        '/purchase-landing/tracking',
        {
          params: {
            campaignName: search.campaignName?.trim() || undefined,
            trkId: search.trkId?.trim() || undefined,
            franchiseIds: search.franchiseIds?.length ? search.franchiseIds.join(',') : undefined,
            page,
            size,
            sort: 'createdAt,desc',
          },
        }
      );
      const { content, ...rest } = response.data;
      set({ codeList: content, codePage: rest });
    } catch (error) {
      console.error(error);
      toast.error('트래킹 코드를 불러오지 못했습니다.');
    }
    set({ loading: false });
  },

  createCode: async (payload: IPurchaseLandingTrackingCodeCreateRequest) => {
    if (!payload.campaignName.trim()) {
      toast.error('캠페인명을 입력해주세요.');
      return;
    }
    if (!payload.packages || payload.packages.length === 0) {
      toast.error('패키지를 1개 이상 추가해주세요.');
      return;
    }
    try {
      await apiClient.post('/purchase-landing/tracking', payload);
      toast.success('새로운 캠페인이 생성되었습니다.');
      const { codePage } = get();
      const pageNumber = codePage?.pageable?.pageNumber ?? 0;
      await get().fetchCodes(pageNumber);
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg ?? '캠페인 생성에 실패했습니다.');
      } else {
        console.error(error);
        toast.error('캠페인 생성에 실패했습니다.');
      }
    }
  },

  updateCode: async (id: number, payload: IPurchaseLandingTrackingCodeUpdateRequest) => {
    try {
      await apiClient.patch(`/purchase-landing/tracking/${id}`, payload);
      toast.success('캠페인 정보가 수정되었습니다.');
      return true;
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg ?? '캠페인 수정에 실패했습니다.');
      } else {
        console.error(error);
        toast.error('캠페인 수정에 실패했습니다.');
      }
      return false;
    }
  },

  deleteCodes: async (ids: number[]) => {
    if (!ids || ids.length === 0) {
      toast.warn('선택된 캠페인이 없습니다.');
      return false;
    }
    try {
      await apiClient.delete('/purchase-landing/tracking', { data: { ids } });
      toast.success(`${ids.length}건의 캠페인이 삭제되었습니다.`);
      const { codePage } = get();
      const pageNumber = codePage?.pageable?.pageNumber ?? 0;
      await get().fetchCodes(pageNumber);
      return true;
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg ?? '캠페인 삭제에 실패했습니다.');
      } else {
        console.error(error);
        toast.error('캠페인 삭제에 실패했습니다.');
      }
      return false;
    }
  },

  reset: () => {
    set({ codeList: [], codePage: null, loading: false, search: {} });
  },
}));
